import { SpanLoading } from '../fetch-status/span-loading';
import { Button, ButtonProps } from '../ui/button';
import { type LoginOptions, useAuth } from '@/client/hooks/use-auth';
import { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type AuthedButtonProps = ButtonProps & {
  showAuth?: boolean;
  showLoading?: boolean;
  isLoading?: boolean;
  loginOptions?: LoginOptions;
};
export const AuthedButton = forwardRef<HTMLButtonElement, AuthedButtonProps>(
  (
    {
      showAuth = true,
      showLoading = true,
      onClick,
      children,
      disabled,
      onKeyUp,
      onKeyDown,
      onKeyPress,
      isLoading,
      loginOptions,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { isAuthLoading, isAuthed, login } = useAuth();
    const handle = useMemo(() => (isAuthed ? onClick : () => login(loginOptions)), [isAuthed, login, loginOptions, onClick]);
    const isDisabled = useMemo(
      () => isLoading || isAuthLoading || (disabled && isAuthed),
      [isLoading, isAuthLoading, disabled, isAuthed]
    );
    return (
      <Button
        type="button"
        {...props}
        disabled={isDisabled}
        ref={ref}
        onKeyUp={isAuthed ? onKeyUp : undefined}
        onKeyDown={isAuthed ? onKeyDown : undefined}
        onKeyPress={isAuthed ? onKeyPress : undefined}
        onClick={handle}
      >
        {showLoading && (isLoading || isAuthLoading) && <SpanLoading />}
        {showAuth && !isAuthed ? t('sign-in') : children}
      </Button>
    );
  }
);

AuthedButton.displayName = 'AuthedButton';
